import { GoogleGenerativeAI } from '@google/generative-ai';

class GeminiTestService {
  private genAI: GoogleGenerativeAI | null = null;

  constructor() {
    const apiKey = process.env.REACT_APP_GOOGLE_AI_API_KEY;
    
    if (apiKey && apiKey !== 'your_google_ai_studio_api_key_here' && apiKey.length > 20) {
      try {
        this.genAI = new GoogleGenerativeAI(apiKey);
      } catch (error) {
        console.error('❌ Fout bij initialiseren Gemini AI:', error);
      }
    }
  }

  async testConnection(): Promise<{ success: boolean; message: string; response?: string }> {
    console.log('🧪 Starting Gemini API test...');
    console.log('- genAI initialized:', !!this.genAI);
    
    if (!this.genAI) {
      const apiKey = process.env.REACT_APP_GOOGLE_AI_API_KEY;
      return {
        success: false,
        message: `Gemini AI niet geïnitialiseerd. API Key: ${apiKey ? 'Aanwezig' : 'Ontbreekt'}`
      };
    }

    try {
      console.log('🧪 Testing Gemini API connection...');
      
      const model = this.genAI.getGenerativeModel({ model: 'gemini-2.5-flash' });
      console.log('📝 Model loaded, generating content...');
      
      const result = await model.generateContent('Say "Hello SuperKonnected!" in Dutch');
      const response = await result.response;
      const text = response.text();
      
      console.log('✅ Gemini API response:', text);
      
      return {
        success: true,
        message: 'Gemini API verbinding succesvol!',
        response: text
      };
    } catch (error: any) {
      console.error('❌ Gemini API error:', error);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        status: error.status,
        stack: error.stack
      });
      
      return {
        success: false,
        message: `Gemini API error: ${error.message || 'Unknown error'}`
      };
    }
  }

  /**
   * Analyseer post-tekst en geef relevante hashtag-suggesties terug via Gemini Flash.
   * Goedkoper dan OpenAI en heeft een gratis tier van 1M tokens/dag.
   */
  async suggestHashtagsFromContent(text: string, collaborationNames: string[] = []): Promise<string[]> {
    if (!this.genAI) return [];
    const trimmed = text.trim();
    if (trimmed.length < 40) return [];

    const collabContext = collaborationNames.length > 0
      ? `De post is gelinkt aan samenwerkingen: ${collaborationNames.join(', ')}.`
      : '';

    const prompt = `Je analyseert de onderstaande post-tekst voor een sociaal platform gericht op samenwerken en communities.
${collabContext}

Geef 5 tot 8 relevante hashtags die de onderwerpen, thema's en context van de tekst goed weergeven.
- Geen hashtags voor triviale of loze woorden
- Gebruik gangbare hashtags (enkelvoud of camelCase, geen spaties)
- Mix van brede en specifieke tags
- Als de tekst placeholder- of nonsenstekst is (bijv. lorem ipsum), geef dan een lege array

Antwoord ALLEEN met een JSON-array van strings zonder #-teken. Geen uitleg.
Voorbeeld: ["Technologie","Auteursrecht","Kunst","AI","Creativiteit"]

Tekst:
${trimmed.slice(0, 2000)}`;

    try {
      const model = this.genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });
      const result = await model.generateContent(prompt);
      const raw = result.response.text().trim();

      // Strip markdown code fences if present
      const jsonStr = raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '').trim();
      let parsed: unknown;
      try {
        parsed = JSON.parse(jsonStr);
      } catch {
        // Try extracting first [...] block from response
        const match = jsonStr.match(/\[[\s\S]*\]/);
        parsed = match ? JSON.parse(match[0]) : [];
      }
      const arr: unknown = Array.isArray(parsed) ? parsed : (
        parsed && typeof parsed === 'object'
          ? ((parsed as any).hashtags ?? (parsed as any).tags ?? Object.values(parsed as object)[0])
          : []
      );
      if (!Array.isArray(arr)) return [];
      return (arr as unknown[])
        .filter((t): t is string => typeof t === 'string' && t.length > 1)
        .map((t: string) => t.replace(/^#+/, '').trim())
        .filter((t: string) => Boolean(t))
        .slice(0, 8);
    } catch {
      return [];
    }
  }

  async generateMatchmakingSuggestion(user1: any, user2: any): Promise<string> {
    if (!this.genAI) {
      throw new Error('Gemini AI niet geïnitialiseerd');
    }

    try {
      const prompt = `
        Analyze these two entrepreneurs and suggest why they should connect:
        
        User 1: ${user1.displayName || 'Unknown'} - ${user1.jobTitle || 'Unknown role'}
        User 2: ${user2.displayName || 'Unknown'} - ${user2.jobTitle || 'Unknown role'}
        
        Provide a brief, professional suggestion in Dutch for why they should connect.
        Keep it under 100 words.
      `;

      const model = this.genAI.getGenerativeModel({ model: 'gemini-2.5-flash' });
      const result = await model.generateContent(prompt);
      const response = await result.response;
      
      return response.text();
    } catch (error: any) {
      console.error('Error generating matchmaking suggestion:', error);
      return 'AI matchmaking suggestion niet beschikbaar op dit moment.';
    }
  }
}

export default new GeminiTestService();

